import { useState } from 'react'
import { Car, Eye, EyeOff, LogIn, Loader } from 'lucide-react'
import { useAppStore } from '../store/useAppStore'
import Button from '../components/ui/Button'
import Card from '../components/ui/Card'
import toast from 'react-hot-toast'
import api from '../services/api'

export default function LoginScreen() {
  const { setCredentials, setStep } = useAppStore()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPwd, setShowPwd]   = useState(false)
  const [loading, setLoading]   = useState(false)

  const handleLogin = async () => {
    if (!username.trim()) return toast.error('Identifiant requis')
    if (!password.trim()) return toast.error('Mot de passe requis')

    setLoading(true)
    try {
      const { data } = await api.post(`/api/auth`, { username, password })
      if (data.success) {
        // Le backend retourne un token opaque (chiffré) qu'on stocke à la place du mot de passe
        setCredentials({ username, password: data.token })
        toast.success(`Connecté — ${data.agentName || username}`)
        setStep('home')
      } else {
        toast.error(data.error || 'Identifiants incorrects')
      }
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Impossible de contacter le serveur'
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-700 to-indigo-900 flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-sm flex flex-col gap-8">

        {/* Logo */}
        <div className="text-center text-white">
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="bg-white/20 p-3 rounded-2xl">
              <Car size={40} className="text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-black tracking-tight">RentalCheck</h1>
          <p className="text-blue-200 mt-2 text-sm">État des lieux véhicule · myrentcar</p>
        </div>

        {/* Formulaire */}
        <Card className="p-6 flex flex-col gap-4">
          <h2 className="text-lg font-bold text-gray-800 text-center">Connexion</h2>

          {/* Username */}
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-600">Identifiant myrentcar</label>
            <input
              type="text"
              value={username}
              onChange={e => setUsername(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleLogin()}
              placeholder="Votre identifiant"
              autoCapitalize="none"
              autoCorrect="off"
              className="border-2 border-gray-200 rounded-xl px-4 py-3 text-gray-800 focus:border-blue-500 focus:outline-none text-base"
            />
          </div>

          {/* Password */}
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-600">Mot de passe</label>
            <div className="relative">
              <input
                type={showPwd ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleLogin()}
                placeholder="••••••••"
                className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 pr-12 text-gray-800 focus:border-blue-500 focus:outline-none text-base"
              />
              <button
                type="button"
                onClick={() => setShowPwd(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
              >
                {showPwd ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <Button onClick={handleLogin} disabled={loading} size="lg" className="w-full mt-2">
            {loading
              ? <><Loader size={20} className="animate-spin" /> Connexion...</>
              : <><LogIn size={20} /> Se connecter</>
            }
          </Button>
        </Card>

        <p className="text-blue-300 text-xs text-center">
          Vos identifiants sont conservés localement<br/>pour maintenir la session active.
        </p>
      </div>
    </div>
  )
}
